import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface DespachoApi {
  id: number;
  clave_despacho_id: number;
  direccion: string;
  fecha_hora_despacho: string;
  observaciones?: string;
  obac_nombre?: string;
  obac_rango?: string;
  conductor_nombre?: string;
  cantidad_voluntarios?: number;
  va_personal_especializado?: boolean;

  // Campos de geocoding
  lat?: number;
  lng?: number;
  geocode_place_id?: string;
  direccion_normalizada?: string;
  geocode_status?: string;
  geocoded_at?: string;

  // Estado/fechas
  activo?: boolean | null;        // tolerante
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface ClaveDespachoApi {
  id: number;
  codigo: string;
  nombre: string;
  categoria: string;
  grupo: string;
  descripcion: string;
  activo: boolean;
  created_at: string;
  updated_at: string;
}

export interface CarroAsignadoApi {
  id: number;        // material_mayor.id
  codigo: string;    // ej: "B-2"
  tipo: string;      // ej: "Carro Bomba"
}

@Injectable({ providedIn: 'root' })
export class DespachosService {
  private baseUrl = environment.apiUrl; // ej: https://api.3dev.cl/api

  constructor(private http: HttpClient) {}

  // =========================
  //  DESPACHOS (LISTADOS)
  // =========================

  /** GET /api/despachos  (lista cruda, sin filtros) */
  obtenerDespachos(): Observable<DespachoApi[]> {
    return this.http.get<DespachoApi[]>(
      `${this.baseUrl}/despachos`,
      { params: this.cacheBusterParams() } // solo params, sin headers extra
    ).pipe(catchError(this.handleError));
  }

  /**
   * GET /api/despachos  (solo activos y no borrados)
   * Reemplaza completamente el array en el consumidor.
   */
  obtenerDespachosActivos(): Observable<DespachoApi[]> {
    return this.obtenerDespachos().pipe(
      map(ds =>
        (ds ?? [])
          // acepta activo: true/undefined/null; excluye solo activo === false
          .filter(d => d.deleted_at == null && d.activo !== false)
          .sort((a, b) => (a.fecha_hora_despacho < b.fecha_hora_despacho ? 1 : -1))
      )
    );
  }

  /** GET /api/despachos/:id */
  obtenerDespachoPorId(id: number): Observable<DespachoApi> {
    return this.http.get<DespachoApi>(
      `${this.baseUrl}/despachos/${id}`,
      { params: this.cacheBusterParams() } // sin headers no-cache (evitar preflight)
    ).pipe(catchError(this.handleError));
  }

  // (Opcional) último activo directo desde la lista
  obtenerUltimoActivo(): Observable<DespachoApi | undefined> {
    return this.obtenerDespachosActivos().pipe(map(ds => ds[0]));
  }

  // =========================
  //  CLAVES DE DESPACHO
  // =========================

  /** GET /api/claves-despacho/:id */
  obtenerClavePorId(id: number): Observable<ClaveDespachoApi> {
    return this.http.get<ClaveDespachoApi>(
      `${this.baseUrl}/claves-despacho/${id}`,
      { params: this.cacheBusterParams() }
    ).pipe(catchError(this.handleError));
  }

  /** GET /api/claves-despacho/codigo/:codigo */
  obtenerClavePorCodigo(codigo: string): Observable<ClaveDespachoApi> {
    return this.http.get<ClaveDespachoApi>(
      `${this.baseUrl}/claves-despacho/codigo/${encodeURIComponent(codigo)}`,
      { params: this.cacheBusterParams() }
    ).pipe(catchError(this.handleError));
  }

  // =========================
  //  GEOCODING / RELACIONES
  // =========================

  /** POST /api/despachos/:id/geocode - Solicita geocodificación para un despacho */
  solicitarGeocodificacion(despachoId: number): Observable<DespachoApi> {
    return this.http.post<DespachoApi>(
      `${this.baseUrl}/despachos/${despachoId}/geocode`,
      {},
      { params: this.cacheBusterParams() }
    ).pipe(catchError(this.handleError));
  }

  /** GET /api/despachos/:id/material-mayor - Carros asignados a un despacho */
  obtenerCarrosAsignados(despachoId: number): Observable<CarroAsignadoApi[]> {
    return this.http.get<CarroAsignadoApi[]>(
      `${this.baseUrl}/despachos/${despachoId}/material-mayor`,
      { params: this.cacheBusterParams() }
    ).pipe(catchError(this.handleError));
  }

  // =========================
  //  HELPERS
  // =========================

  private handleError(err: any) {
    return throwError(() => err);
  }

  private cacheBusterParams(): HttpParams {
    return new HttpParams().set('t', Date.now().toString());
  }
}
